import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

export const ONBOARDING_STEP_IDS = [
  'organization_setup',
  'sender_domain',
  'sender_email',
  'connect_integration',
  'import_contacts',
  'test_email',
] as const;

export type OnboardingStepId = (typeof ONBOARDING_STEP_IDS)[number];

export interface OnboardingStepState {
  id: OnboardingStepId;
  required: boolean;
  completed: boolean;
  skipped: boolean;
  active: boolean;
}

export interface OnboardingState {
  id: string;
  organizationId: string;
  completedSteps: Partial<Record<OnboardingStepId, boolean>>;
  skippedSteps: Partial<Record<OnboardingStepId, boolean>>;
  senderIdentity: SenderIdentityState | null;
  senderDomain: SenderDomainState | null;
  currentStep: OnboardingStepId;
  completedAt: string | null;
  completion: number;
  isComplete: boolean;
  nextStep: OnboardingStepId;
  steps: OnboardingStepState[];
}

export interface SenderIdentityState {
  id: string | null;
  email: string | null;
  name: string | null;
  status: string;
  verifiedAt: string | null;
  source: 'identity' | 'settings';
}

export interface SenderDomainState {
  id: string;
  domain: string;
  status: 'PENDING' | 'PARTIAL' | 'VERIFIED' | 'FAILED' | string;
  verifiedAt: string | null;
}

export interface SenderDomain {
  id: string;
  domain: string;
  status: 'PENDING' | 'PARTIAL' | 'VERIFIED' | 'FAILED';
  dnsRecords: Record<string, DnsRecord>;
  verifiedAt: string | null;
}

export interface DnsRecord {
  type: string;
  host: string;
  value: string;
  status?: 'PASS' | 'FAIL';
  reason?: string;
}

export interface TrackingVerification {
  verified: boolean;
  token: string;
  instructions?: string;
  nextStep?: OnboardingStepId;
}

export interface SenderEmailResult {
  id: string;
  email: string;
  name: string;
  status: string;
  verifiedAt: string | null;
  verificationSent: boolean;
  expiresAt: string;
}

const POLL_MS = 30_000;

export function useOnboardingState(orgId: string | null) {
  return useQuery({
    queryKey: ['onboarding', orgId, 'state'],
    enabled: Boolean(orgId),
    refetchInterval: (query) => (query.state.data?.isComplete ? false : POLL_MS),
    queryFn: async () => {
      const response = await api.get<OnboardingState>('/onboarding/state');
      return response.data;
    },
  });
}

export function useOnboardingActions(orgId: string | null) {
  const queryClient = useQueryClient();

  function invalidate(): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: ['onboarding', orgId, 'state'] });
  }

  const completeStep = useMutation({
    mutationFn: async (stepId: OnboardingStepId) => {
      const response = await api.post<OnboardingState>(`/onboarding/steps/${stepId}/complete`);
      return response.data;
    },
    onSuccess: invalidate,
  });

  const skipStep = useMutation({
    mutationFn: async (stepId: OnboardingStepId) => {
      const response = await api.post<OnboardingState>(`/onboarding/steps/${stepId}/skip`);
      return response.data;
    },
    onSuccess: invalidate,
  });

  const verifyTracking = useMutation({
    mutationFn: async (domain: string) => {
      const response = await api.post<TrackingVerification>('/onboarding/verify-tracking', { domain });
      return response.data;
    },
    onSuccess: invalidate,
  });

  const addSenderEmail = useMutation({
    mutationFn: async (payload: { email: string; name: string }) => {
      const response = await api.post<SenderEmailResult>('/onboarding/add-sender-email', payload);
      return response.data;
    },
    onSuccess: invalidate,
  });

  const sendTestEmail = useMutation({
    mutationFn: async (to: string) => {
      const response = await api.post<{ sent: boolean; nextStep?: OnboardingStepId }>(
        '/onboarding/send-test-email',
        { to },
      );
      return response.data;
    },
    onSuccess: invalidate,
  });

  return { completeStep, skipStep, verifyTracking, addSenderEmail, sendTestEmail };
}

export async function createSenderDomain(domain: string): Promise<SenderDomain> {
  const response = await api.post<SenderDomain>('/settings/sender-domains', { domain });
  return response.data;
}

export async function verifySenderDomain(id: string): Promise<{ records: DnsRecord[] }> {
  const response = await api.post<{ records: DnsRecord[] }>(`/settings/sender-domains/${id}/verify`);
  return response.data;
}

export function onboardingProgress(state: OnboardingState | undefined): { done: number; total: number } {
  const done = ONBOARDING_STEP_IDS.filter(
    (step) => state?.completedSteps[step] || state?.skippedSteps[step],
  ).length;
  return { done, total: ONBOARDING_STEP_IDS.length };
}
